"use client";

import {JSX, useState} from "react";

import {GuestReviews} from "@/sections/rental/guest-reviews/guest-reviews";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {StarRating} from "@/shared/ui/data-display/rating";
import {RatingBreakdown} from "@/shared/ui/data-display/rating";
import {SingleReview} from "@/shared/ui/data-display/reviews";
import {Typography} from "@/shared/ui/data-display/typography";

import {ReviewsSearch} from "./parts/reviews-search";

import "./styles/rating-review.scss";

interface Review {
    id: number;
    name: string;
    rating: number;
    date: string;
    reviewText: string;
    managerResponse?: string;
}

export interface RatingReviewProps {
    overallRating: number;
    totalReviews: number;
    guestSayingText: string;
    breakdown: {
        fiveStar: number;
        fourStar: number;
        threeStar: number;
        twoStar: number;
        oneStar: number;
    };
    reviews: Array<Review>;
    className?: string;
}

const REVIEWS_PER_PAGE = 6;

export function RatingReview({overallRating, totalReviews, guestSayingText, breakdown, reviews, className = ""}: RatingReviewProps): JSX.Element {
    const {t} = useTranslation("reviews");
    const {t: tCommon} = useTranslation("common");
    const [visibleReviewsCount, setVisibleReviewsCount] = useState(REVIEWS_PER_PAGE);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("recent");

    // Filter reviews based on search
    const filteredReviews = reviews.filter((review) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return review.reviewText.toLowerCase().includes(query) || review.name.toLowerCase().includes(query);
    });

    // Sort reviews
    const sortedReviews = [...filteredReviews].sort((a, b) => {
        switch (sortBy) {
            case "highest":
                return b.rating - a.rating;
            case "lowest":
                return a.rating - b.rating;
            case "oldest":
                // Sort by date: oldest first (ascending)
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            case "recent":
            default:
                // Sort by date: most recent first (descending)
                return new Date(b.date).getTime() - new Date(a.date).getTime();
        }
    });

    // Get visible reviews
    const visibleReviews = sortedReviews.slice(0, visibleReviewsCount);
    const hasMoreReviews = visibleReviewsCount < sortedReviews.length;

    const handleLoadMore = (): void => {
        setVisibleReviewsCount((prev) => prev + REVIEWS_PER_PAGE);
    };

    const handleSearch = (query: string): void => {
        setSearchQuery(query);
        setVisibleReviewsCount(REVIEWS_PER_PAGE); // Reset visible count on search
    };

    const handleSort = (sortOption: string): void => {
        setSortBy(sortOption);
    };

    return (
        <section id="reviews-section" className={`rating-review ${className}`}>
            {/* Rating Header */}
            <div className="rating-review__header">
                <Typography as="h2" size={{desktop: 24, mobile: 20}} weight={600} className="rating-review__title">
                    {t("title")}
                </Typography>
                <div className="rating-review__rating">
                    <StarRating name={overallRating} rating={overallRating} size="large" />
                </div>
            </div>

            {/* Guest Reviews */}
            <div className="rating-review__guest-reviews">
                <GuestReviews reviewText={guestSayingText} showRating={false} />
            </div>

            {/* Rating Breakdown */}
            <div className="rating-review__breakdown">
                <RatingBreakdown totalReviews={totalReviews} breakdown={breakdown} />
            </div>

            {/* Search and Sort */}
            <div className="rating-review__search">
                <ReviewsSearch onSearch={handleSearch} onSort={handleSort} />
            </div>

            {/* Reviews List */}
            <div className="rating-review__list">
                {visibleReviews.length > 0 ? (
                    visibleReviews.map((review) => (
                        <SingleReview
                            key={review.id}
                            name={review.name}
                            rating={review.rating}
                            date={review.date}
                            reviewText={review.reviewText}
                            managerResponse={review.managerResponse}
                        />
                    ))
                ) : (
                    <Typography as="p" size={16} weight={400} className="rating-review__no-results">
                        {reviews.length === 0 ? t("noReviews") : t("noResults")}
                    </Typography>
                )}
            </div>

            {hasMoreReviews && (
                <div className="rating-review__load-more">
                    <ButtonBase variant="secondary-outline" size="sm" onClick={handleLoadMore} className="rating-review__button">
                        {tCommon("showMore")}
                    </ButtonBase>
                </div>
            )}
        </section>
    );
}
